import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { BaseState, withCrudOperations } from '../crud.state';
import { withGeoDataSelectors } from './geo-data.selectors';
import { GeoDataService } from '../../services/geo-data/geo-data.service';
import {
  KeyValueDistricts,
  GeoData,
  FeatureCollection,
} from '../../models/geo-data';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { downloadBlob } from '../../utility/download';
import {
  filterGeoJsonFeaturesByGroup,
  getDistrictsFromGeoData,
  getDistrictsFromGeoDatas,
  getDistrictsFromSelection,
  getFeatureCollectionFromSelection,
  getGroupsFromDistricts,
} from './geo-utils';
import { withLogging } from '../features/logger.feature';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { ConfirmationDialogService } from '../../services/confirmation-dialog.service';

export type GeoDataState = BaseState<GeoData>;

export const initialState: GeoDataState = {
  items: {},
  loading: false,
  initialized: false,
  itemsLoaded: false,
  selectedId: '',
  selection: [],
  version: 0,
  lastPersistedId: '',
  trigger: undefined,
  entityType: 'GeoData',
  entityLabel: 'GeoData',
  filter: {
    query: '',
    category: [],
    tags: undefined,
    products: undefined,
    published: null,
    locked: null,
    ids: undefined,
  },
  events: {
    add: ['linkDataLayer'],
    update: ['linkDataLayer'],
  },
};

export const GeoDataStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCrudOperations<GeoData>(GeoDataService),
  withLogging(initialState.entityType),
  withGeoDataSelectors(),
  withMethods(
    (
      store,
      geoDataService = inject(GeoDataService),
      confirmationDialogService = inject(ConfirmationDialogService)
    ) => ({
      findMany: rxMethod<{
        filterByKeys: string[];
      }>(
        pipe(
          switchMap(({ filterByKeys }) => {
            return geoDataService.findMany(filterByKeys).pipe(
              tapResponse({
                next: response => {
                  patchState(store, {
                    items: response,
                    loading: false,
                    initialized: true,
                    itemsLoaded: true,
                  });
                },
                error: (error: any) => {
                  patchState(store, {
                    loading: false,
                    initialized: true,
                    error,
                  });
                  confirmationDialogService.addErrorMessage(
                    `${store.entityLabel()} konnten nicht geladen werden.`,
                    'Daten konnten nicht geladen werden'
                  );
                },
              })
            );
          })
        )
      ),
      generate(
        features: KeyValueDistricts,
        type: 'Polygon' | 'Point'
      ): GeoData {
        return geoDataService.generateEmptyGeoData(features, type);
      },
      generateByFeatures(
        features: GeoJSON.Feature[],
        currentFeatures?: GeoJSON.Feature[],
        group?: string
      ) {
        return geoDataService.generateByFeatures(
          features,
          currentFeatures,
          group
        );
      },
      downloadSelected(filename: string) {
        geoDataService.downloadGeoJson(filename, store.geoJsonFromSelection());
      },
      filterGeoJsonByGroup(
        featureCollection: FeatureCollection,
        groupKey: string
      ) {
        return filterGeoJsonFeaturesByGroup(featureCollection, groupKey);
      },
      getDistrictsById(
        id: string,
        filterByGroupKey?: string
      ): KeyValueDistricts | null {
        return getDistrictsFromGeoData(store.items()[id], filterByGroupKey);
      },
      getDistrictsByIds(ids: string[], filterByGroupKey?: string) {
        return getDistrictsFromGeoDatas(
          ids.map(id => store.items()[id]).filter(geo => !!geo),
          filterByGroupKey
        );
      },
      getGroupsById(id: string) {
        const features = getDistrictsFromGeoData(store.items()[id]);
        return features ? getGroupsFromDistricts(features) : null;
      },
      getDefaultGroupById(id: string): string {
        const geoData = store.items()[id];
        if (!geoData.geoJson) return '';
        try {
          const collection = JSON.parse(geoData.geoJson);
          return collection.group || '';
        } catch (e) {
          return '';
        }
      },
      getDistrictByIdAndKey(id: string, key: string) {
        const districts = this.getDistrictsById(id);
        return districts && districts[key] ? districts[key] : null;
      },
      getFirstDistrictById(id: string) {
        const districts = this.getDistrictsById(id);
        if (!districts) return null;
        const firstDistrict = Object.values(districts)[0];
        return firstDistrict || null;
      },
      hasGroup(id: string, group: string) {
        return !!this.getGroupsById(id)?.includes(group);
      },
      getGeoGroupMenu(
        id: string,
        dataLayerId: string,
        addEmpty: boolean,
        callBack: any
      ): MenuItem | null {
        const groups = this.getGroupsById(id);
        if (!groups || groups.length === 0) return null;
        const groupItems = [
          {
            icon: 'fas fa-xmark',
            label: 'abwählen',
            command: (event: MenuItemCommandEvent): void => {
              callBack({ group: undefined, dataLayerId });
            },
          },
          {
            separator: true,
          },
        ].concat(
          groups.map((group: string) => {
            return {
              icon: 'fas fa-circle-nodes',
              label: group,
              command: (event: MenuItemCommandEvent): void => {
                callBack({ group, dataLayerId });
              },
            };
          })
        );

        return {
          icon: 'fas fa-filter',
          label: 'Gruppe',
          items: groupItems,
        };
      },
      convertToXlsx: rxMethod<{ poiRows: Array<string[]>; filename: string }>(
        pipe(
          switchMap(({ poiRows, filename }) => {
            return geoDataService.convertToXlsx(poiRows, filename).pipe(
              tapResponse({
                next: blob => {
                  patchState(store, {
                    selection: [],
                  });
                  downloadBlob(blob, filename);
                },
                error: console.error,
                //finalize: () => patchState(store, { loading: false }),
              })
            );
          })
        )
      ),
    })
  ),
  withComputed(({ selectedItem }) => ({
    getFeatureCollectionFromSelection:
      getFeatureCollectionFromSelection(selectedItem),
    getDistrictsFromSelection: getDistrictsFromSelection(selectedItem),
  })),
  withHooks({
    //onInit({ loadAllItemsByPromise }) {
    //  loadAllItemsByPromise(); // is done in initialization handler
    //},
    onDestroy() {
      console.log('on destroy');
    },
  })
);
